import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/**
 * Вычислить расстояние между двумя точками (Haversine formula)
 * @param {number} lat1 - Широта первой точки
 * @param {number} lon1 - Долгота первой точки
 * @param {number} lat2 - Широта второй точки
 * @param {number} lon2 - Долгота второй точки
 * @returns {number} - Расстояние в километрах
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Радиус Земли в километрах
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

/**
 * Hook для загрузки отфильтрованных мест
 *
 * @param {number} categoryId - ID категории
 * @param {Array} selectedTagIds - Массив ID выбранных тегов
 * @param {Object} userLocation - Местоположение пользователя {latitude, longitude}
 * @param {number} initialPageSize - Начальный размер страницы (по умолчанию 10)
 * @returns {Object} - Объект с местами и состоянием загрузки
 */
export const useFilteredPlaces = (categoryId, selectedTagIds = [], userLocation = null, initialPageSize = 10) => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const loadPlacesRef = useRef(null);
  const prevTagsRef = useRef([]);
  const prevCategoryRef = useRef(null);
  const selectedTagIdsRef = useRef(selectedTagIds);

  /**
   * Загрузить места
   */
  const loadPlaces = useCallback(async (page = 1, append = false) => {
    if (!categoryId) {
      setPlaces([]);
      setTotalCount(0);
      return;
    }

    // Отменить предыдущий запрос
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Построить query параметры
      const params = new URLSearchParams({
        'filters[category][id][$eq]': categoryId,
        'filters[published][$eq]': 'true',
        'pagination[page]': page.toString(),
        'pagination[pageSize]': initialPageSize.toString(),
      });

      // Добавить фильтры по тегам
      const currentTagIds = selectedTagIdsRef.current || [];
      if (currentTagIds && currentTagIds.length > 0) {
        currentTagIds.forEach((tagId, index) => {
          params.append(`filters[tags][id][$in][${index}]`, tagId);
        });
      }

      const response = await api.get(`/places/filtered?${params.toString()}`, {
        signal: abortControllerRef.current.signal,
      });

      const result = response.data;
      let loadedPlaces = result.data || [];

      // Если есть местоположение пользователя, добавить расстояние и отсортировать
      if (userLocation && userLocation.latitude && userLocation.longitude) {
        loadedPlaces = loadedPlaces.map(place => ({
          ...place,
          distance: place.latitude && place.longitude
            ? calculateDistance(
                userLocation.latitude,
                userLocation.longitude,
                place.latitude,
                place.longitude
              )
            : null,
        }));

        // Сортировать по расстоянию
        loadedPlaces.sort((a, b) => {
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        });
      }

      const total = result.meta?.pagination?.total || 0;

      if (append) {
        setPlaces(prev => [...prev, ...loadedPlaces]);
      } else {
        setPlaces(loadedPlaces);
      }

      setTotalCount(total);
      setPagination(result.meta?.pagination || null);
      setCurrentPage(page);
      setHasMore(loadedPlaces.length + (append ? places.length : 0) < total);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        // Запрос был отменен, игнорируем ошибку
        return;
      }
      setError(err.message);
      if (!append) {
        setPlaces([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
    }
    // places.length намеренно не в deps: loadPlaces пересоздавался бы
    // после каждой загрузки и уходил в цикл
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, userLocation, initialPageSize]);

  // Сохраняем loadPlaces в ref для использования в useEffect без зависимостей
  loadPlacesRef.current = loadPlaces;

  // Обновляем ref при изменении selectedTagIds
  useEffect(() => {
    selectedTagIdsRef.current = selectedTagIds;
  }, [selectedTagIds]);

  /**
   * Refetch - принудительная перезагрузка
   */
  const refetch = useCallback(() => {
    loadPlaces();
  }, [loadPlaces]);

  /**
   * Подсчет результатов (для модального окна)
   */
  const getResultsCount = useCallback(async (catId, tagIds = []) => {
    if (!catId) return 0;

    try {
      const params = new URLSearchParams({
        'filters[category][id][$eq]': catId,
        'filters[published][$eq]': 'true',
        'pagination[page]': '1',
        'pagination[pageSize]': '1', // Минимальный размер для получения count
      });

      if (tagIds && tagIds.length > 0) {
        tagIds.forEach((tagId, index) => {
          params.append(`filters[tags][id][$in][${index}]`, tagId);
        });
      }

      const response = await api.get(`/places/filtered?${params.toString()}`);

      const result = response.data;
      return result.meta?.pagination?.total || 0;
    } catch (err) {
      return 0;
    }
  }, []);

  // Загрузка при изменении параметров (сброс на первую страницу)
  useEffect(() => {
    // Не загружаем если нет categoryId
    if (!categoryId) {
      setPlaces([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    // Проверяем реальное изменение параметров
    const categoryChanged = prevCategoryRef.current !== categoryId;
    const tagsChanged = JSON.stringify(prevTagsRef.current) !== JSON.stringify(selectedTagIds);

    // Загружаем места при первом монтировании или при изменении параметров
    if (categoryChanged || tagsChanged) {
      prevCategoryRef.current = categoryId;
      prevTagsRef.current = selectedTagIds;

      setCurrentPage(1);
      setPlaces([]);
      if (loadPlacesRef.current) {
        loadPlacesRef.current(1, false);
      }
    }

    // Cleanup
    return () => {
      // Читаем ref в момент вызова cleanup, а не при запуске эффекта:
      // таймер ставится позже, и копия его бы не увидела. Правило
      // предполагает ref на DOM-узел, здесь же — id таймера
      if (debounceTimerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(debounceTimerRef.current);
      }
      // НЕ отменяем запросы в cleanup - это вызывает проблемы
      // if (abortControllerRef.current) {
      //   abortControllerRef.current.abort();
      // }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, selectedTagIds]);

  /**
   * Загрузить следующую страницу
   */
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadPlaces(currentPage + 1, true);
    }
  }, [loading, hasMore, currentPage, loadPlaces]);

  return {
    places,
    loading,
    error,
    totalCount,
    pagination,
    refetch,
    getResultsCount,
    loadMore,
    hasMore,
    currentPage,
  };
};

export default useFilteredPlaces;
